// The /storybook marketing's pictures. OWNER-APPROVED ONLY (2026-09-28): a
// drawing is shown on the site, the home page, link previews or the ads only
// if the owner picked it as a clear likeness, because "without a clear match
// it defeats the purpose". The owner judged every sample drawing and picked
// ONE: the 3D animated cover of Mia and her mom on a hill above a seaside town
// with a red kite (bench file PR_L13_kite_animated3d_cover.jpg), published as
// style-animated3d. Every other drawing is retired and stays off.
//
// The drawing is the storybook engine's own, unretouched output, drawn from
// SYNTHETIC sample "photos" of a made-up family (sample family F3): Mia (5),
// her mom Ana and her dad Tom. They are not real people and not customers, so
// every photo is shown under a "Sample photos" label. Tom is not in the
// approved drawing, so nothing may show a drawing of him; his sample photo
// only appears where the page explains that grown-ups can be added.
//
// Files and sizes: public/storybook/promo/manifest.json (built by
// scripts/storybook-promo.cjs; public, so it carries no provenance). Where
// each picture came from, the owner's pick and the retired list:
// marketing/storybook-ads/promo-provenance.json (never published). The face
// points below are the only copy. promo.test.ts fails if a picture referenced
// here is missing from the manifest, or if the manifest holds any drawing
// other than style-animated3d: adding one takes a deliberate manifest change.
// Alt text lives in the locale bundles (storybookPage.promo.alt.*) so it
// follows the page language.

const BASE = '/storybook/promo';

export interface PromoImage {
  /** Smallest file: the fallback src when srcset is ignored. */
  src: string;
  srcSet: string;
  /** Intrinsic size of the largest file (every file is square). */
  width: number;
  height: number;
  /** i18n key of the alt text. */
  altKey: string;
}

function illustration(file: string, alt: string): PromoImage {
  return {
    src: `${BASE}/${file}-640.webp`,
    srcSet: `${BASE}/${file}-640.webp 640w, ${BASE}/${file}-1200.webp 1200w`,
    width: 1200,
    height: 1200,
    altKey: `storybookPage.promo.alt.${alt}`,
  };
}

function photo(file: string, alt: string): PromoImage {
  return {
    src: `${BASE}/${file}-240.webp`,
    srcSet: `${BASE}/${file}-240.webp 240w, ${BASE}/${file}-480.webp 480w`,
    width: 480,
    height: 480,
    altKey: `storybookPage.promo.alt.${alt}`,
  };
}

export type SamplePerson = 'mia' | 'ana' | 'tom';

/** The made-up family's first names (the same in every language). */
export const SAMPLE_NAMES: Record<SamplePerson, string> = { mia: 'Mia', ana: 'Ana', tom: 'Tom' };

/** The sample "photos" (synthetic people, never customers). */
export const SAMPLE_PHOTOS: Record<SamplePerson, PromoImage> = {
  mia: photo('photo-mia', 'photoMia'),
  ana: photo('photo-ana', 'photoAna'),
  tom: photo('photo-tom', 'photoTom'),
};

/**
 * THE approved drawing (the owner's pick, 2026-09-28): 3D animated movie
 * style, Mia in her yellow raincoat and red boots holding the kite string,
 * her mom Ana in a green sweater beside her, on a grassy hill above a seaside
 * town. The only illustration any surface may show.
 */
export const APPROVED_DRAWING = illustration('style-animated3d', 'styleAnimated3d');

/**
 * The art styles the site offers: only 3D (owner, 2026-09-29: the only style
 * whose faces clearly matched the photos). services/storybook/config.js still
 * defines the retired styles so books already ordered in them finish.
 */
export const STYLE_ORDER = ['animated3d'] as const;
export type StyleId = (typeof STYLE_ORDER)[number];

/** The art style the approved drawing is in. */
export const APPROVED_DRAWING_STYLE: StyleId = 'animated3d';

/** The sample picture of the offered style: the approved drawing. */
export const STYLE_SAMPLES: Partial<Record<StyleId, PromoImage>> = {
  [APPROVED_DRAWING_STYLE]: APPROVED_DRAWING,
};

/** Same emoji the builder shows next to the style. */
export const STYLE_EMOJI: Record<StyleId, string> = {
  animated3d: '🎬',
};

export interface FacePoint {
  /** Centre of the face, as a fraction of the square picture. */
  x: number;
  y: number;
  /** Magnification that frames head and shoulders in a square. */
  zoom: number;
}

/** The sample people the approved drawing shows (Tom is not in it). */
export type DrawnPerson = Extract<SamplePerson, 'mia' | 'ana'>;
export const DRAWN_PEOPLE: readonly DrawnPerson[] = ['mia', 'ana'];

/**
 * Where Mia's and Ana's faces sit in the approved drawing, as fractions of the
 * square picture (measured 2026-09-28 on the 1408 px render; the published
 * files are plain square resizes of it). x, y is the middle of the
 * head-and-shoulders square: the nose, a little lower for Ana so her
 * shoulders and necklace show; zoom 3.8 frames each head about as large as in
 * the sample photos.
 */
export const APPROVED_DRAWING_FACES: Record<DrawnPerson, FacePoint> = {
  mia: { x: 0.415, y: 0.5, zoom: 3.8 },
  ana: { x: 0.598, y: 0.355, zoom: 3.8 },
};

/** Each drawn face close-up: always cut from the approved drawing. */
export const DRAWN_FACES: Record<DrawnPerson, { image: PromoImage; face: FacePoint }> = {
  mia: { image: APPROVED_DRAWING, face: APPROVED_DRAWING_FACES.mia },
  ana: { image: APPROVED_DRAWING, face: APPROVED_DRAWING_FACES.ana },
};

/** The face a parent would tap in each sample photo: [left, top, right, bottom] as fractions. */
export const PHOTO_FACE_BOXES: Record<SamplePerson, [number, number, number, number]> = {
  mia: [0.31, 0.2, 0.71, 0.73],
  ana: [0.32, 0.15, 0.69, 0.67],
  tom: [0.28, 0.16, 0.72, 0.74],
};

/**
 * Redraw rounds a parent gets while checking the finished pictures: the
 * server's STORYBOOK_MAX_PROOF_ROUNDS default (services/storybook/config.js).
 * /config does not expose it, so keep the two in step.
 */
export const PROOF_REDRAW_ROUNDS = 3;
